import { motion } from "framer-motion";
import { BookOpen, Terminal, ArrowRight } from "lucide-react";
import { PageShell } from "@/components/layout/PageShell";

const DOCS_SECTIONS = [
  { title: "Getting Started", desc: "Set up your development environment and create your first QA agent in under 5 minutes.", tag: "Beginner" },
  { title: "Agent Architecture", desc: "Learn how QA agents work under the hood — prompts, tools, evaluation pipelines, and more.", tag: "Core" },
  { title: "Benchmark Submission", desc: "Submit your agent to public benchmarks. Understand scoring, retries, and evaluation criteria.", tag: "Core" },
  { title: "SDK Reference", desc: "Full reference for the Python and TypeScript SDKs. Includes all methods, types, and examples.", tag: "Reference" },
  { title: "CI/CD Integration", desc: "Deploy agents in your pipeline using GitHub Actions, GitLab CI, or any CI/CD platform.", tag: "Advanced" },
  { title: "Custom Benchmarks", desc: "Create private benchmarks for your team. Define scenarios, metrics, and scoring weights.", tag: "Enterprise" },
];

export default function DocumentationPage() {
  return (
    <PageShell>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <span className="text-xs font-mono text-arena-green uppercase tracking-[0.2em]">Resources</span>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl text-white mt-3 tracking-tight">Documentation</h1>
          <p className="mt-4 text-base font-mono text-zinc-500 max-w-2xl">Everything you need to build and deploy QA agents.</p>
        </motion.div>

        {/* Quick start */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mt-12 p-8 bg-arena-surface border border-white/[0.06] rounded-md"
        >
          <div className="flex items-center gap-3 mb-4">
            <Terminal className="w-5 h-5 text-arena-blue" />
            <span className="font-heading font-semibold text-white">Quick Start</span>
          </div>
          <div className="bg-arena-base border border-white/[0.06] rounded-md p-4 font-mono text-sm">
            <div className="text-zinc-500"># Install the QA Arena CLI</div>
            <div className="text-arena-green mt-1">$ pip install qa-arena</div>
            <div className="text-zinc-500 mt-3"># Initialize a new agent project</div>
            <div className="text-arena-green mt-1">$ qa-arena init my-agent</div>
            <div className="text-zinc-500 mt-3"># Submit to benchmarks</div>
            <div className="text-arena-green mt-1">$ qa-arena submit --benchmark ui-testing</div>
          </div>
        </motion.div>

        {/* Docs grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
          {DOCS_SECTIONS.map((doc, i) => (
            <motion.div
              key={doc.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.05 }}
              data-testid={`docs-card-${i}`}
              className="group p-6 bg-arena-surface border border-white/[0.06] rounded-md hover:border-white/[0.12] transition-colors cursor-pointer"
            >
              <div className="flex items-center justify-between mb-3">
                <BookOpen className="w-4 h-4 text-zinc-500 group-hover:text-arena-green transition-colors" />
                <span className="text-[10px] font-mono text-zinc-600 uppercase tracking-wider">{doc.tag}</span>
              </div>
              <h3 className="font-heading font-semibold text-white text-base mb-2">{doc.title}</h3>
              <p className="text-sm font-mono text-zinc-500 leading-relaxed">{doc.desc}</p>
              <div className="mt-4 flex items-center gap-1 text-xs font-mono text-zinc-600 group-hover:text-zinc-400 transition-colors">
                Read more <ArrowRight className="w-3 h-3" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </PageShell>
  );
}
